// frontend/src/api.js
// Central API client for AI Governance Platform
// Module 8 update: user management, invite flow, and Stripe checkout endpoints
// IP-009 update: acceptInvite now accepts and forwards agreedToTerms,
// so the Master Subscription Agreement acceptance reaches the backend.
// commit: feat(ip009): forward agreedToTerms in acceptInvite call
//
// Security patch: file downloads no longer put the login token in a URL.
// downloadFile sends it in the Authorization header and saves the response in
// the browser, so the token cannot be recorded in server logs, browser
// history, or a referrer header.
//
// Login fix: a wrong password now shows the server's message on the login page.
// Before, the 401 from the login route was treated as an ended session and the
// page reloaded with no message.

const BASE_URL = process.env.REACT_APP_API_URL || '';

// Routes whose 401 answer is a normal result, not an ended session.
const SESSION_EXEMPT_PATHS = ['/api/auth/login'];

// ---------------------------------------------------------------------------
// HTTP helpers
// ---------------------------------------------------------------------------
function getToken() {
  return localStorage.getItem('token');
}

async function request(method, path, body = null) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const options = { method, headers };
  if (body) options.body = JSON.stringify(body);

  const res = await fetch(`${BASE_URL}${path}`, options);

  // A 401 normally means the session has ended, so the token is cleared and the
  // person is sent to the login page. The login route itself answers a wrong
  // password with 401. That is an ordinary result the login page must show, so
  // it is passed through as an error with the server's message.
  if (res.status === 401 && !SESSION_EXEMPT_PATHS.includes(path)) {
    localStorage.removeItem('token');
    window.location.href = '/login';
    return;
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw { message: data.message || data.error || `Request failed: ${res.status}`, trialExpired: data.trialExpired || false };
  return data;
}

async function requestMultipart(method, path, formData) {
  const token = getToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE_URL}${path}`, { method, headers, body: formData });

  if (res.status === 401) {
    localStorage.removeItem('token');
    window.location.href = '/login';
    return;
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed: ${res.status}`);
  return data;
}

// Downloads a file through an authenticated request and saves it in the
// browser. The file name comes from the server's Content-Disposition header;
// fallbackName is used only when that header cannot be read.
async function downloadFile(path, fallbackName) {
  const token = getToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE_URL}${path}`, { headers });

  if (res.status === 401) {
    localStorage.removeItem('token');
    window.location.href = '/login';
    return;
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || data.message || `Download failed: ${res.status}`);
  }

  const disposition = res.headers.get('Content-Disposition') || '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = match ? match[1] : fallbackName;

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
export const login = (email, password) =>
  request('POST', '/api/auth/login', { email, password });

export const register = (payload) =>
  request('POST', '/api/auth/register', payload);

export const getMe = () =>
  request('GET', '/api/auth/me');

// ---------------------------------------------------------------------------
// Module 1: AI Vendor Register
// ---------------------------------------------------------------------------
export const getVendors = () =>
  request('GET', '/api/vendors');

export const getVendor = (id) =>
  request('GET', `/api/vendors/${id}`);

export const createVendor = (payload) =>
  request('POST', '/api/vendors', payload);

export const updateVendor = (id, payload) =>
  request('PUT', `/api/vendors/${id}`, payload);

export const deleteVendor = (id) =>
  request('DELETE', `/api/vendors/${id}`);

// ---------------------------------------------------------------------------
// Module 2: Control Evidence Locker
// ---------------------------------------------------------------------------
export const getControls = () =>
  request('GET', '/api/controls');

export const getControl = (id) =>
  request('GET', `/api/controls/${id}`);

export const updateControl = (id, payload) =>
  request('PUT', `/api/controls/${id}`, payload);

export const uploadEvidence = (id, file) => {
  const fd = new FormData();
  fd.append('evidence', file);
  return requestMultipart('POST', `/api/controls/${id}/evidence`, fd);
};

export const removeEvidence = (id) =>
  request('DELETE', `/api/controls/${id}/evidence`);

export const downloadEvidence = (id) =>
  downloadFile(`/api/controls/${id}/evidence/download`, 'evidence');

// ---------------------------------------------------------------------------
// Module 4: AI Incident Register
// ---------------------------------------------------------------------------
export const getIncidents = () =>
  request('GET', '/api/incidents');

export const getIncident = (id) =>
  request('GET', `/api/incidents/${id}`);

export const createIncident = (payload) =>
  request('POST', '/api/incidents', payload);

export const updateIncident = (id, payload) =>
  request('PUT', `/api/incidents/${id}`, payload);

export const deleteIncident = (id) =>
  request('DELETE', `/api/incidents/${id}`);

export const uploadIncidentAttachment = (id, file) => {
  const fd = new FormData();
  fd.append('attachment', file);
  return requestMultipart('POST', `/api/incidents/${id}/attachment`, fd);
};

// ---------------------------------------------------------------------------
// Module 3: Board Pack Generator
// ---------------------------------------------------------------------------
export const getBoardPacks = () =>
  request('GET', '/api/board-packs');

export const generateBoardPack = (payload) =>
  request('POST', '/api/board-packs/generate', payload);

export const downloadBoardPack = (id) =>
  downloadFile(`/api/board-packs/${id}/download`, 'board-pack.pdf');

export const uploadAttestation = (id, file) => {
  const fd = new FormData();
  fd.append('attestation', file);
  return requestMultipart('POST', `/api/board-packs/${id}/attest`, fd);
};

export const deleteBoardPack = (id) =>
  request('DELETE', `/api/board-packs/${id}`);

// ---------------------------------------------------------------------------
// Regulatory Reference Management (Week 6)
// ---------------------------------------------------------------------------
export const getRegulatoryRefs = () =>
  request('GET', '/api/regulatory-refs');

export const getRegulatoryRef = (id) =>
  request('GET', `/api/regulatory-refs/${id}`);

export const updateRegulatoryRef = (id, payload) =>
  request('PUT', `/api/regulatory-refs/${id}`, payload);

export const getRegulatoryRefHistory = (id) =>
  request('GET', `/api/regulatory-refs/${id}/history`);

// ---------------------------------------------------------------------------
// Module 5: Notifications (Week 7)
// ---------------------------------------------------------------------------
export const getNotifications = () =>
  request('GET', '/api/notifications');

export const getNotificationCount = () =>
  request('GET', '/api/notifications/count');

export const dismissNotification = (id) =>
  request('PUT', `/api/notifications/${id}/dismiss`);

export const markAllNotificationsRead = () =>
  request('PUT', '/api/notifications/mark-all-read');

export const markNotificationRead = (id) =>
  request('PUT', `/api/notifications/${id}/read`);

// ---------------------------------------------------------------------------
// Module 5: Regulatory Alerts — platform owner only (Week 7)
// ---------------------------------------------------------------------------
export const getRegulatoryAlerts = () =>
  request('GET', '/api/regulatory-alerts');

export const reviewRegulatoryAlert = (id, payload) =>
  request('PUT', `/api/regulatory-alerts/${id}/review`, payload);

export const getMonitorSources = () =>
  request('GET', '/api/regulatory-alerts/sources');

export const markManualSourceReviewed = (id) =>
  request('PUT', `/api/regulatory-alerts/sources/${id}/manual-review`);

export const triggerMonitorRun = () =>
  request('POST', '/api/regulatory-alerts/run-check');

// ---------------------------------------------------------------------------
// Module 6: Org Notes on Regulatory Clauses (Week 8)
// ---------------------------------------------------------------------------
export const getOrgNotes = () =>
  request('GET', '/api/org-notes');

export const upsertOrgNote = (refId, payload) =>
  request('PUT', `/api/org-notes/${refId}`, payload);

export const deleteOrgNote = (refId) =>
  request('DELETE', `/api/org-notes/${refId}`);

// ---------------------------------------------------------------------------
// Users (org members — for owner dropdowns)
// ---------------------------------------------------------------------------
export const getOrgUsers = () =>
  request('GET', '/api/vendors/users/list');

// ---------------------------------------------------------------------------
// Module 8: User Management
// ---------------------------------------------------------------------------

/** List all active users in the caller's org. Admin only. */
export const getUsers = () =>
  request('GET', '/api/users');

/** Update a user's role. Admin only. */
export const updateUserRole = (id, role) =>
  request('PATCH', `/api/users/${id}/role`, { role });

/** Soft-delete a user. Admin only. */
export const removeUser = (id) =>
  request('DELETE', `/api/users/${id}`);

// ---------------------------------------------------------------------------
// Module 8: Invite Flow
// ---------------------------------------------------------------------------

/** Admin sends an invite to a new user. */
export const inviteUser = (payload) =>
  request('POST', '/api/invite', payload);

/**
 * Verify an invite token before rendering the accept form.
 * Returns { name, email, role, orgName } or throws if invalid/expired.
 */
export const verifyInvite = (token) =>
  request('GET', `/api/invite/verify?token=${encodeURIComponent(token)}`);

/**
 * Invitee sets their password, accepts the Master Subscription
 * Agreement, and activates the account.
 * IP-009: agreedToTerms is now a required third argument, forwarded
 * to POST /api/invite/accept alongside token and password.
 */
export const acceptInvite = (token, password, agreedToTerms) =>
  request('POST', '/api/invite/accept', { token, password, agreedToTerms });

// ---------------------------------------------------------------------------
// Module 8: Stripe
// ---------------------------------------------------------------------------

/**
 * Creates a Stripe Checkout session for the given priceId.
 * Returns { url } — redirect to this URL to start checkout.
 */
export const createCheckoutSession = (priceId) =>
  request('POST', '/api/stripe/checkout', { priceId });

// ---------------------------------------------------------------------------
// Default export — axios-compatible interface
// ---------------------------------------------------------------------------
async function axiosWrap(method, path, payload) {
  const fullPath = path.startsWith('/api') ? path : `/api${path}`;
  const data = await request(method, fullPath, payload || null);
  return { data };
}

const api = {
  get:    (path)          => axiosWrap('GET',    path),
  post:   (path, payload) => axiosWrap('POST',   path, payload),
  put:    (path, payload) => axiosWrap('PUT',    path, payload),
  delete: (path)          => axiosWrap('DELETE', path),
};

export default api;
